import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { VisibilityOffIcon, VisibilityOnIcon } from "@/modules/common/assets/icons";
import { BrandLogo } from "@/modules/common/assets/logos";
import { Button, Input } from "@/modules/common/components";
import useAdminSignIn from "./hooks/useAdminSignIn";
import { signInFormData, signInSchema } from "./schemas";
import ForgotPasswordModal from "./components/ForgotPasswordModal";
import FirstAccessModal from "./components/FirstAccessModal";
import EnergyMotion from "./components/EnergyMotion";

const SignIn = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [isFirstAccessOpen, setIsFirstAccessOpen] = useState(false);
  const signIn = useAdminSignIn();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<signInFormData>({ resolver: zodResolver(signInSchema) });

  const onSubmit = async (values: signInFormData) => {
    await signIn.mutateAsync(values);
  };

  return (
    <section className="flex h-screen w-full items-stretch justify-center overflow-y-auto bg-gray-100 md:p-4">
      <div className="grid w-full grid-cols-1 overflow-hidden bg-white md:rounded-2xl lg:grid-cols-[45fr_55fr]">
        <div className="flex flex-col items-center justify-center px-6 py-8 sm:px-10 md:px-16 md:py-10">
          <div className="w-full max-w-[420px]">
            <BrandLogo className="mb-6" />

            <span className="mb-4 inline-flex w-fit items-center gap-2 rounded-full border border-ui-border bg-ui-hover px-3.5 py-1.5 text-xs font-semibold text-ui-text">
              <span className="h-1.5 w-1.5 rounded-full bg-[#99C32C]" />
              Portal Admin
            </span>

            <h1 className="mb-2 text-[28px] font-extrabold tracking-tight text-ui-text-dark md:text-[33px]">Bem-vindo de volta</h1>
            <p className="mb-5 border-b border-ui-border pb-5 text-sm text-ui-text-muted">Preencha seu e-mail e senha para continuar</p>

            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <Input
                  label="E-mail"
                  data-test="email"
                  maskType="email"
                  placeholder="Digite seu e-mail"
                  {...register("email", { required: true })}
                />
                {errors.email && (
                  <p className="mt-1 text-xs text-red-500" data-test="emailError">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <Input
                  label="Senha"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="Digite sua senha"
                  data-test="password"
                  icon={
                    <button
                      type="button"
                      className="flex cursor-pointer items-center"
                      onClick={() => setIsPasswordVisible((state) => !state)}
                      aria-label={isPasswordVisible ? "Ocultar senha" : "Mostrar senha"}
                    >
                      {isPasswordVisible ? <VisibilityOnIcon className="w-5 h-5" /> : <VisibilityOffIcon className="w-5 h-5" />}
                    </button>
                  }
                  iconPosition="right"
                  {...register("password", { required: true })}
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-500" data-test="passwordError">
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                <Button
                  type="button"
                  data-test="forgot-password-link"
                  variant="text"
                  label="Esqueci minha senha"
                  onClick={() => setIsForgotPasswordOpen(true)}
                />

                <Button
                  type="button"
                  data-test="first-access-link"
                  variant="text"
                  label="Primeiro acesso"
                  onClick={() => setIsFirstAccessOpen(true)}
                />
              </div>

              <Button
                type="submit"
                data-test="login-button"
                color="success"
                disabled={signIn.isPending}
                loading={signIn.isPending}
                className="w-full"
                label="Entrar"
              />
            </form>
          </div>
        </div>

        <EnergyMotion />
      </div>

      <ForgotPasswordModal
        open={isForgotPasswordOpen}
        onOpenChange={setIsForgotPasswordOpen}
        onPasswordReset={(email) => setValue("email", email)}
      />

      <FirstAccessModal
        open={isFirstAccessOpen}
        onOpenChange={setIsFirstAccessOpen}
        onPasswordReset={(email) => setValue("email", email)}
      />
    </section>
  );
};

export default SignIn;
